import Vec2 from 'vec2';

import {
  getOutlinePointsFromModifiedShape,
  isPointInsidePolygon,
} from '~/core/helpers';
import {
  ApplianceModel,
  ApplianceSource,
  ApplianceType,
  DrawingAppliance,
  DrawingShape,
} from '~/core/models';

/**
 * Gets the approximate outline of an appliance as a polygon of Vec2 points
 * Used for collision detection and auto-placement calculations
 */
export const getApproximateApplianceOutline = (
  appliance: DrawingAppliance
): Vec2[] => [
  new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y + 22),
  new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y - 22),
  new Vec2(appliance.centerPoint.x - 22, appliance.centerPoint.y - 22),
  new Vec2(appliance.centerPoint.x - 22, appliance.centerPoint.y + 22),
  new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y + 22),
];

export const getCutoutsCountFromShape = (
  drawingShapes: DrawingShape[],
  cutoutId: number
) =>
  drawingShapes.reduce(
    (count: number, shape: DrawingShape) =>
      count +
      shape.appliances.filter((appliance: DrawingAppliance) => {
        const { productId, applianceType } = appliance.applianceModel;
        // Only count explicit cutout types (ELECTRICAL_CUTOUT, FAUCET_CUTOUT)
        // Exclude sink labor products and other non-cutout appliances
        const isExplicitCutout =
          applianceType === ApplianceType.ELECTRICAL_CUTOUT ||
          applianceType === ApplianceType.FAUCET_CUTOUT;

        return productId === cutoutId && isExplicitCutout;
      }).length,
    0
  );

interface isCooktopOrRangeProps {
  applianceModel?: ApplianceModel;
  applianceType?: string;
}

export const isCooktop = ({
  applianceModel,
  applianceType,
}: isCooktopOrRangeProps) => {
  const type = applianceModel?.type ?? applianceType ?? '';

  return type.toUpperCase() === ApplianceType.COOKTOP;
};

export const isRange = ({
  applianceModel,
  applianceType,
}: isCooktopOrRangeProps) => {
  const type = applianceModel?.type ?? applianceType ?? '';

  return type.toUpperCase() === ApplianceType.RANGE;
};

/**
 * Determines if an appliance is customer-supplied based on its productSource
 * @param appliance The appliance to check
 * @returns true if the appliance is customer-supplied
 */
export const isCustomerSuppliedAppliance = (
  appliance?: ApplianceModel
): boolean => appliance?.productSource === ApplianceSource.CUSTOMER;

/**
 * Determines if an appliance is browsed (not customer-supplied) based on its productSource
 * @param appliance The appliance to check
 * @returns true if the appliance is browsed (IN_STORE or PROGRAM)
 */
export const isBrowsedAppliance = (appliance: ApplianceModel): boolean =>
  appliance.productSource === ApplianceSource.IN_STORE ||
  appliance.productSource === ApplianceSource.PROGRAM;

export const isCutoutAppliance = (appliance: ApplianceModel) =>
  [ApplianceType.ELECTRICAL_CUTOUT, ApplianceType.FAUCET_CUTOUT].includes(
    appliance.applianceType
  );

/**
 * Represents the bounding box of an appliance
 */
interface ApplianceBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Gets the bounding box of an appliance at a given center point
 */
export const getApplianceBounds = (
  centerPoint: Vec2,
  appliance: DrawingAppliance
): ApplianceBounds => ({
  left: centerPoint.x - appliance.width / 2,
  right: centerPoint.x + appliance.width / 2,
  top: centerPoint.y - appliance.height / 2,
  bottom: centerPoint.y + appliance.height / 2,
});

/**
 * Checks if two bounding boxes overlap
 */
export const boundsOverlap = (
  a: ApplianceBounds,
  b: ApplianceBounds
): boolean =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/**
 * Checks if an appliance at a given position collides with any other appliances
 */
export const hasCollisionWithAppliances = (
  position: Vec2,
  movingAppliance: DrawingAppliance,
  otherAppliances: DrawingAppliance[]
): boolean => {
  const movingBounds = getApplianceBounds(position, movingAppliance);

  return otherAppliances.some((other) => {
    if (other.id === movingAppliance.id) {
      return false;
    }
    const otherBounds = getApplianceBounds(
      new Vec2(other.centerPoint.x, other.centerPoint.y),
      other
    );

    return boundsOverlap(movingBounds, otherBounds);
  });
};

/**
 * Checks if an appliance at a given center position fits entirely within the shape boundary.
 * This prevents appliances from being placed where their edges extend outside the shape.
 */
const isApplianceFullyInsideShape = (
  centerPosition: Vec2,
  appliance: DrawingAppliance,
  shapeOutline: Vec2[]
): boolean => {
  const halfWidth = appliance.width / 2;
  const halfHeight = appliance.height / 2;

  // Check all four corners of the appliance bounding box
  const corners = [
    new Vec2(centerPosition.x - halfWidth, centerPosition.y - halfHeight), // top-left
    new Vec2(centerPosition.x + halfWidth, centerPosition.y - halfHeight), // top-right
    new Vec2(centerPosition.x - halfWidth, centerPosition.y + halfHeight), // bottom-left
    new Vec2(centerPosition.x + halfWidth, centerPosition.y + halfHeight), // bottom-right
  ];

  return corners.every((corner) => isPointInsidePolygon(shapeOutline, corner));
};

// ============================================================================
// GRID SEARCH TYPE DEFINITIONS
// ============================================================================

/**
 * Represents a valid position with its distance from target.
 */
interface ValidPositionCandidate {
  position: Vec2;
  distanceSq: number;
}

/**
 * Context for performing grid search.
 */
interface GridSearchContext {
  targetPosition: Vec2;
  appliance: DrawingAppliance;
  shapeOutline: Vec2[];
  otherAppliances: DrawingAppliance[];
  searchRadius: number;
  step: number;
}

// ============================================================================
// GRID SEARCH HELPER FUNCTIONS
// ============================================================================

/**
 * Checks if a position is valid based on boundary mode.
 * - Strict mode: All 4 corners must be inside shape
 * - Fallback mode: Only center point must be inside shape
 *
 * NOTE: Parameter order differs between underlying functions:
 * - isApplianceFullyInsideShape: (position, appliance, shapeOutline)
 * - isPointInsidePolygon: (shapeOutline, position)
 */
const isPositionInsideBoundary = (
  position: Vec2,
  appliance: DrawingAppliance,
  shapeOutline: Vec2[],
  useStrictBoundary: boolean
): boolean =>
  useStrictBoundary
    ? isApplianceFullyInsideShape(position, appliance, shapeOutline)
    : isPointInsidePolygon(shapeOutline, position);

/**
 * Checks if target position is valid and returns it if so.
 * Returns null if target position is not valid.
 */
const tryTargetPosition = (
  context: GridSearchContext,
  useStrictBoundary: boolean
): Vec2 | null => {
  const { targetPosition, appliance, shapeOutline, otherAppliances } = context;

  const isInsideBoundary = isPositionInsideBoundary(
    targetPosition,
    appliance,
    shapeOutline,
    useStrictBoundary
  );

  const hasCollision = hasCollisionWithAppliances(
    targetPosition,
    appliance,
    otherAppliances
  );

  return isInsideBoundary && !hasCollision ? targetPosition : null;
};

/**
 * Performs grid search to find all valid positions within search radius.
 * Returns array of valid position candidates (unsorted).
 *
 * Uses circle optimization: only iterates over points inside the search circle
 * (~23% fewer iterations than a full square grid).
 */
const collectValidPositions = (
  context: GridSearchContext,
  useStrictBoundary: boolean
): ValidPositionCandidate[] => {
  const {
    targetPosition,
    appliance,
    shapeOutline,
    otherAppliances,
    searchRadius,
    step,
  } = context;

  const validPositions: ValidPositionCandidate[] = [];
  const searchRadiusSq = searchRadius * searchRadius;

  for (let dx = -searchRadius; dx <= searchRadius; dx += step) {
    const dxSq = dx * dx;
    const maxDy = Math.floor(Math.sqrt(searchRadiusSq - dxSq) / step) * step;

    for (let dy = -maxDy; dy <= maxDy; dy += step) {
      const distanceSq = dxSq + dy * dy;
      const testPos = new Vec2(targetPosition.x + dx, targetPosition.y + dy);

      const isInsideBoundary = isPositionInsideBoundary(
        testPos,
        appliance,
        shapeOutline,
        useStrictBoundary
      );

      const hasNoCollision = !hasCollisionWithAppliances(
        testPos,
        appliance,
        otherAppliances
      );

      if (isInsideBoundary && hasNoCollision) {
        validPositions.push({ position: testPos, distanceSq });
      }
    }
  }

  return validPositions;
};

/**
 * Finds the closest valid position from candidates.
 * Returns null if no candidates available.
 */
const findClosestFromCandidates = (
  candidates: ValidPositionCandidate[]
): Vec2 | null => {
  if (candidates.length === 0) {
    return null;
  }

  candidates.sort((a, b) => a.distanceSq - b.distanceSq);

  return candidates[0].position;
};

/**
 * Performs a single grid search pass with specified boundary mode.
 * First checks target position, then performs grid search if needed.
 */
const performGridSearchPass = (
  context: GridSearchContext,
  useStrictBoundary: boolean
): Vec2 | null => {
  // Try target position first (most common case - early return optimization)
  const targetResult = tryTargetPosition(context, useStrictBoundary);

  if (targetResult) {
    return targetResult;
  }

  // Grid search for closest valid position
  const candidates = collectValidPositions(context, useStrictBoundary);

  return findClosestFromCandidates(candidates);
};

// ============================================================================
// MAIN EXPORTED FUNCTION
// ============================================================================

/**
 * Finds the closest valid position for an appliance that doesn't collide with others
 * and stays within the shape boundary.
 * Returns the target position if valid, or the closest valid position found,
 * or null if no valid position can be found within the search radius.
 *
 * Uses a two-pass approach:
 * 1. First pass: Strict boundary checking (all 4 corners must be inside shape)
 * 2. Second pass (fallback): Center-point-only boundary checking (for legacy compatibility)
 *
 * This ensures appliances can still be placed on shapes that have existing appliances
 * which were placed before the strict boundary checking was implemented.
 */
export const findClosestValidPosition = (
  targetPosition: Vec2,
  appliance: DrawingAppliance,
  shape: DrawingShape,
  otherAppliances: DrawingAppliance[]
): Vec2 | null => {
  const shapeOutline = getOutlinePointsFromModifiedShape(shape);

  const context: GridSearchContext = {
    targetPosition,
    appliance,
    shapeOutline,
    otherAppliances,
    searchRadius: 200, // pixels - covers most shape sizes
    step: 4, // pixels - larger step for faster search over bigger radius
  };

  // PASS 1: Try strict boundary checking (all 4 corners must be inside shape)
  const strictResult = performGridSearchPass(context, true);

  if (strictResult) {
    return strictResult;
  }

  // PASS 2: Fallback to center-point-only boundary checking
  // This handles cases where:
  // - Existing appliances were placed before strict checking was implemented
  // - Shape is too narrow for strict checking but can still fit appliances
  return performGridSearchPass(context, false);
};
